/**
 * Colour stitching (group tag siblings): matches legacy SAL ColourSwatches / ColourSwatchesProduct.
 * Loads sibling products via /collections/all/{groupTag}?view=json or single product ?view=json.
 */

function stitchingRoutes() {
  const r = typeof Theme !== 'undefined' ? Theme.routes : {};
  const root_url = r.root_url || '';
  /** @type {string} */
  let all_products = r.all_products_collection_url || '/collections/all';
  if (all_products.endsWith('/')) {
    all_products = all_products.slice(0, -1);
  }
  return { root_url, all_products_collection_url: all_products };
}

function variantImageSrc(variant) {
  if (!variant) return '';
  if (variant.featured_image?.src) return variant.featured_image.src;
  const im = variant.image;
  if (typeof im === 'string') return im;
  return im?.src || '';
}

function productFeaturedSrc(product) {
  const fi = product.featured_image;
  if (typeof fi === 'string') return fi;
  return fi?.src || '';
}

/** Liquid JSON image fields (`featured_image`, `second_image`) can be a string URL or `{ src }`. */
function productImageSrcFromLiquidJson(img) {
  if (img == null) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object' && 'src' in img && typeof /** @type {{ src?: string }} */ (img).src === 'string') {
    return /** @type {{ src: string }} */ (img).src;
  }
  return '';
}

/**
 * Shopify CDN URLs often differ only by query; treat same path as one image.
 * @param {string} url
 */
function imagePathKey(url) {
  if (!url) return '';
  try {
    const u =
      url.startsWith('//') ? new URL(`https:${url}`)
      : /^https?:\/\//i.test(url) ? new URL(url)
      : new URL(url, 'https://cdn.shopify.com');
    return `${u.hostname}${u.pathname}`;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

/**
 * Second PLP hover image from `templates/collection.json.liquid` (`second_image` / `images[1]`).
 * @param {Record<string, unknown>} product
 * @param {string} primarySrc
 */
function productSecondHoverSrc(product, primarySrc) {
  let s = productImageSrcFromLiquidJson(product.second_image);
  if (!s || imagePathKey(s) === imagePathKey(primarySrc)) return '';
  return s;
}

function fetchStitchingJson(settings) {
  const { all_products_collection_url } = stitchingRoutes();
  if (!settings.groupTag || settings.groupTag === 'undefined') {
    return Promise.reject(new Error('colour-stitching: missing groupTag'));
  }
  const url = `${all_products_collection_url}/${encodeURIComponent(settings.groupTag)}?view=json`;
  return fetch(url).then((response) => response.json());
}

function readSettings(self) {
  const script = self.querySelector('script[type="application/json"][data-colour-stitch-settings]');
  if (!script?.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

function waitForSettings(self) {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      const s = readSettings(self);
      if (s) {
        clearInterval(interval);
        resolve(s);
      }
    }, 100);
    setTimeout(() => {
      clearInterval(interval);
      resolve(readSettings(self));
    }, 3000);
  });
}

function findCardGallery(settings) {
  return document.querySelector(`.card-gallery[data-product-id="${settings.id}"]`);
}

/**
 * Instant colourway switch — show new before hiding old so nothing flashes underneath.
 *
 * @param {HTMLElement} gallery
 * @param {string} [stitchHandle]
 * @returns {boolean}
 */
function selectStitchColourway(gallery, stitchHandle) {
  /** @type {HTMLElement | null} */
  let next = null;

  if (stitchHandle) {
    const esc =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(stitchHandle)
        : stitchHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const found = gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${esc}"]`);
    if (found instanceof HTMLElement) next = found;
  }

  if (!next) {
    const fallback = gallery.querySelector('.colour-stitching__colourway');
    if (fallback instanceof HTMLElement) next = fallback;
  }

  if (!next) return false;

  const prev = gallery.querySelector('.colour-stitching__colourway.selected');
  if (prev === next) return true;

  next.classList.add('selected');
  if (prev instanceof HTMLElement) prev.classList.remove('selected');
  return true;
}

/**
 * @param {Record<string, unknown> | undefined} product
 * @param {string} stitchHandle
 * @param {string} baseHandle
 * @param {string | null} originalUrlAttr
 */
function resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr) {
  const sh = (stitchHandle || '').toString();
  const bh = (baseHandle || '').toString();
  if (sh === bh && originalUrlAttr) return originalUrlAttr;

  const raw = product && typeof product.url === 'string' ? product.url : '';
  if (!raw) return originalUrlAttr || '';

  /** @type {string} */
  let pathPlusSearch;
  try {
    const u = raw.startsWith('http') ? new URL(raw) : new URL(raw, window.location.origin);
    pathPlusSearch = `${u.pathname}${u.search}`;
  } catch {
    pathPlusSearch = raw.startsWith('/') ? raw : `/${raw}`;
  }

  const variants = product && Array.isArray(product.variants) ? /** @type {Record<string, unknown>[]} */ (product.variants) : [];
  const vid = variants[0]?.id;
  if (vid != null && pathPlusSearch && !pathPlusSearch.includes('variant=')) {
    pathPlusSearch += (pathPlusSearch.includes('?') ? '&' : '?') + `variant=${vid}`;
  }

  return pathPlusSearch;
}

/**
 * @param {HTMLElement} card
 * @param {HTMLElement} gallery
 * @param {string} stitchHandle
 * @param {Record<string, unknown> | undefined} product
 * @param {Record<string, unknown>} settings
 */
function applyStitchNavigationToCard(card, gallery, stitchHandle, product, settings) {
  gallery.dataset.activeStitchHandle = (stitchHandle || '').toString();

  const baseHandle = ((settings.handle || '') + '').toString();
  const originalUrlAttr = card.getAttribute('data-stitch-original-url');
  const path = resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr);

  if (!path) return;

  let absolute;
  try {
    absolute =
      path.startsWith('http') ? path : new URL(path.startsWith('/') ? path : `/${path}`, window.location.origin).href;
  } catch {
    absolute = path;
  }

  /** @type {NodeListOf<HTMLAnchorElement> | null} */
  const mains = card.querySelectorAll('[ref="productCardLink"], a.product-card__link');
  mains.forEach((a) => {
    if (a instanceof HTMLAnchorElement) a.href = absolute;
  });

  /** @type {HTMLAnchorElement | null} */
  const galleryLink = gallery.querySelector('a[ref="cardGalleryLink"]');
  if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = absolute;

  /** @type {HTMLAnchorElement | null} */
  const titleLink = card.querySelector('a[ref="productTitleLink"]');
  if (titleLink instanceof HTMLAnchorElement) titleLink.href = absolute;

  if (product && card.getAttribute('data-product-transition') === 'true') {
    if (stitchHandle === baseHandle) {
      const origFeatured = card.dataset.stitchOrigFeaturedMediaUrl;
      if (origFeatured) card.setAttribute('data-featured-media-url', origFeatured);
    } else {
      const featSrc = productFeaturedSrc(product);
      if (featSrc) card.setAttribute('data-featured-media-url', featSrc);
    }
  }

  /** @type {{ refs?: { quickAdd?: { fetchProductPage: (url: string) => unknown }}} | null} */
  const pcAny = /** @type {*} */ (card);
  pcAny?.refs?.quickAdd?.prefetch?.();
}

class ColourSwatches extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector('.colour-stitching__container');
    if (!this.container || this.classList.contains('locked')) return;

    waitForSettings(this).then((settings) => {
      if (!this.container) return;
      if (settings == null) {
        console.warn('colour-stitch-plp: missing JSON settings');
        return;
      }

      this.classList.add('locked');

      fetchStitchingJson(settings)
        .then((responseJson) => {
          this.container.classList.add('loaded');
          if (!Array.isArray(responseJson)) {
            responseJson = responseJson.products;
          }
          if (!responseJson || responseJson.length <= 1) {
            this.classList.add('hidden');
            this.nextElementSibling?.classList.remove('hidden');
          } else {
            if (window.location.href.search('/products') === -1) {
              this.nextElementSibling?.classList.add('hidden');
            }
            this._stitchProductsForCard = Array.isArray(responseJson) ? responseJson : [];
            this.initSwatches(responseJson, settings);
            this.initMainImages(responseJson, settings);
          }
          this.classList.remove('locked');
        })
        .catch((e) => {
          console.warn(e);
          this.classList.remove('locked');
        });
    });
  }

  /**
   * @param {Record<string, unknown>[]} products
   * @param {Record<string, unknown>} settings
   */
  initMainImages(products, settings) {
    const scope = this.closest('product-card');
    const gallery =
      scope?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) || findCardGallery(settings);
    if (!gallery) return;
    if (gallery.querySelector('.colour-stitching__image-layer .colour-stitching__colourway')) return;

    let html = '';
    for (const product of products) {
      const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
      const variant = variants[0];
      const handleStr = ((product.handle || '') + '').toString();
      const src = productFeaturedSrc(product) || variantImageSrc(variant);
      if (!src) continue;
      const secondSrc = productSecondHoverSrc(/** @type {Record<string, unknown>} */ (product), src);
      const hasSecond = secondSrc ? 'true' : 'false';
      const title =
        variant && typeof variant.title === 'string' ? variant.title : (product.handle || '').toString();
      const escTitle = title.replace(/"/g, '&quot;');
      const prim = `<img data-stitch-handle="${handleStr}" data-variant-id="${variant?.id ?? ''}" src="${src}" alt="${escTitle}" class="new-swatch-images new-swatch-images--primary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`;
      const sec = secondSrc
        ? `<img data-stitch-handle="${handleStr}" src="${secondSrc}" alt="" class="new-swatch-images new-swatch-images--secondary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`
        : '';
      html += `<div class="colour-stitching__colourway" data-stitch-handle="${handleStr}" data-has-second="${hasSecond}">${prim}${sec}</div>`;
    }

    /** @type {string} */
    const currentHandle = ((settings.handle || '') + '').toString();

    if (!html.trim()) return;

    if (scope instanceof HTMLElement && scope.getAttribute('data-product-transition') === 'true') {
      const curFeat = scope.getAttribute('data-featured-media-url');
      if (curFeat && !scope.dataset.stitchOrigFeaturedMediaUrl) {
        scope.dataset.stitchOrigFeaturedMediaUrl = curFeat;
      }
    }
    const firstSlideImg = gallery.querySelector('slideshow-slide:not([aria-hidden="true"]) img, slideshow-slide:first-child img');
    if (firstSlideImg instanceof HTMLImageElement) {
      firstSlideImg.classList.remove('blur-up', 'fade-in');
    }

    const layer =
      gallery.querySelector('.colour-stitching__image-layer') || document.createElement('div');
    if (!layer.classList.contains('colour-stitching__image-layer')) {
      layer.className = 'colour-stitching__image-layer';
      layer.setAttribute('aria-hidden', 'true');
      gallery.appendChild(layer);
    }
    layer.innerHTML = html;

    const escapedHandle =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(currentHandle)
        : currentHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const selected =
      currentHandle &&
      gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${escapedHandle}"]`);
    /** @type {HTMLElement | null | undefined} */
    const fallback = layer.querySelector('.colour-stitching__colourway');

    /** @type {HTMLElement | undefined} */
    let toPick;
    if (selected instanceof HTMLElement) toPick = selected;
    else if (fallback instanceof HTMLElement) toPick = fallback;

    let overlayReady = selectStitchColourway(/** @type {HTMLElement} */ (gallery), currentHandle);
    if (!overlayReady && toPick instanceof HTMLElement) {
      layer.querySelectorAll('.colour-stitching__colourway').forEach((n) => n.classList.remove('selected'));
      toPick.classList.add('selected');
      overlayReady = true;
    }

    if (!overlayReady) return;

    gallery.classList.add('card-gallery--has-colour-stitching', 'card-gallery--stitch-overlay-ready');
    if (gallery.hasAttribute('on:pointerenter')) {
      gallery.classList.add('card-gallery--stitch-respects-hover-preview');
    }
    gallery.dataset.cardBaseHandle = currentHandle;
    gallery.dataset.activeStitchHandle = currentHandle;
  }

  /**
   * @param {Record<string, unknown>[]} products
   * @param {Record<string, unknown>} settings
   */
  initSwatches(products, settings) {
    let html = '';
    const cardRoot = this.closest('product-card');
    const intraVariantSwatches = cardRoot?.querySelector('product-swatches');

    products.forEach((product) => {
      const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
      const variant = variants[0];
      const img = this.getProductImage(product, settings);
      /** @type {string} */
      const handleStr = ((product.handle || '') + '').toString();
      const isSelectedClass = settings.handle === handleStr ? 'selected' : '';

      const hasNullPrice = product.price === 0 || product.price === undefined;

      const el = `<div class="colour-stitching__swatch-item thumbnail ${isSelectedClass}" role="button" tabindex="0" data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}">
          <span class="colour-stitching__swatch-thumb">${img}</span>
        </div>`;

      html += el;
    });

    if (intraVariantSwatches instanceof HTMLElement && html) {
      intraVariantSwatches.classList.add('hidden');
    }

    if (html) this.container.innerHTML = html;

    const swatches = this.container.querySelectorAll('.colour-stitching__swatch-item');
    const scopeCard = this.closest('product-card');
    swatches.forEach((swatch) => {
      const activate = (event) => {
        event.preventDefault();
        event.stopPropagation();
        /** @type {string | undefined} */
        const stitchHandle = swatch.dataset.handle;
        const mainImageContainer =
          scopeCard?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) ||
          document.querySelector(`.card-gallery[data-product-id='${settings.id}']`);
        if (mainImageContainer instanceof HTMLElement && stitchHandle) {
          selectStitchColourway(mainImageContainer, stitchHandle);
        }

        if (
          scopeCard instanceof HTMLElement &&
          mainImageContainer instanceof HTMLElement &&
          stitchHandle &&
          typeof this._stitchProductsForCard !== 'undefined'
        ) {
          const matched = this._stitchProductsForCard.find(
            (p) => ((p.handle ?? '') + '').toString() === stitchHandle,
          );
          applyStitchNavigationToCard(
            scopeCard,
            mainImageContainer,
            stitchHandle,
            matched,
            settings,
          );
        }

        swatches.forEach((item) => item.classList.remove('selected'));
        swatch.classList.add('selected');
      };
      swatch.addEventListener('click', activate);
      swatch.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') activate(e);
      });
    });
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {Record<string, unknown>} settings
   */
  getProductImage(product, settings) {
    let imageSrc = productFeaturedSrc(product);

    switch (settings.image_type) {
      case 'featured_image':
        imageSrc = productFeaturedSrc(product);
        break;
      case 'metafield': {
        const swImg = product.swatch_image || product.metafield_swatch_image;
        const extracted = productImageSrcFromLiquidJson(swImg);
        if (extracted) {
          imageSrc = extracted;
        } else {
          imageSrc = productFeaturedSrc(product);
        }
        break;
      }
      case 'first_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        /** @type {Record<string, unknown> | undefined} */
        const v0 = variants[0];
        if (v0?.image && typeof v0.image === 'object' && /** @type {{ src?: string }} */ (v0.image).src) {
          imageSrc = /** @type {{ src: string }} */ (v0.image).src;
        } else if (v0?.image && typeof v0.image === 'string') {
          imageSrc = /** @type {string} */ (v0.image);
        }
        break;
      }
      case 'last_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const last = variants[variants.length - 1];
        if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
          imageSrc = /** @type {{ src: string }} */ (last.image).src;
        }
        break;
      }
      default:
        break;
    }

    return `<img src="${imageSrc}" data-image-second="" alt="" width="40" height="40" loading="lazy" decoding="async" />`;
  }
}

customElements.define('colour-swatches-stitch-plp', ColourSwatches);

class ColourSwatchesProduct extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector('.colour-stitching__container');
    if (!this.container || this.classList.contains('locked')) return;

    waitForSettings(this).then((settings) => {
      if (!this.container) return;
      if (settings == null) {
        console.warn('colour-stitch-pdp: missing JSON settings');
        return;
      }

      this.classList.add('locked');

      fetchStitchingJson(settings)
        .then((responseJson) => {
          this.container.classList.add('loaded');
          if (!Array.isArray(responseJson)) {
            responseJson = responseJson.products;
          }
          if (!responseJson || responseJson.length <= 1) {
            this.classList.add('hidden');
            this.nextElementSibling?.classList?.remove('hidden');
          } else {
            if (window.location.href.search('/products') === -1) {
              this.nextElementSibling?.classList?.add('hidden');
            }
            this.initSwatches(responseJson, settings);
            this.setCurrent();
          }
          this.classList.remove('locked');
        })
        .catch((e) => {
          console.warn(e);
          this.classList.remove('locked');
        });
    });
  }

  /** @param {Record<string, unknown>[]} products @param {Record<string, unknown>} settings */
  initSwatches(products, settings) {
    let html = '';
    for (const product of products) {
      const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
      const variant = variants[0];
      const img = this.getProductImage(product, settings);
      /** @type {string} */
      const handleStr = ((product.handle || '') + '').toString();
      const hasNullPrice = product.price === 0;
      const url = typeof product.url === 'string' ? product.url : '#';
      html += `<a class="colour-stitching__swatch-item colour-stitching__swatch-link" data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}" href="${url}" title="${handleStr.replace(/"/g, '&quot;')}">${img}</a>`;
    }
    if (html) this.container.innerHTML = html;
  }

  /** @param {Record<string, unknown>} product @param {Record<string, unknown>} settings */
  getProductImage(product, settings) {
    let imageSrc = productFeaturedSrc(product);

    switch (settings.image_type) {
      case 'featured_image':
        imageSrc = productFeaturedSrc(product);
        break;
      case 'metafield': {
        const swImg = product.swatch_image || product.metafield_swatch_image;
        const extracted = productImageSrcFromLiquidJson(swImg);
        if (extracted) {
          imageSrc = extracted;
        } else {
          imageSrc = productFeaturedSrc(product);
        }
        break;
      }
      case 'first_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const first = variants[0];
        if (first?.image && typeof first.image === 'object' && /** @type {{ src?: string }} */ (first.image).src) {
          imageSrc = /** @type {{ src: string }} */ (first.image).src;
        } else if (first?.image && typeof first.image === 'string') {
          imageSrc = /** @type {string} */ (first.image);
        }
        break;
      }
      case 'last_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const last = variants[variants.length - 1];
        if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
          imageSrc = /** @type {{ src: string }} */ (last.image).src;
        }
        break;
      }
      default:
        break;
    }

    return `<img src="${imageSrc}" alt="" loading="lazy" decoding="async" width="44" height="44" />`;
  }

  setCurrent() {
    const handle =
      window.current?.product?.handle ||
      (typeof window.theme?.currentProduct === 'string' ? window.theme.currentProduct : '');
    if (!handle || !this.container) return;
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const current = /** @type {HTMLAnchorElement | null} */ (
      this.container.querySelector(`a[data-handle="${escaped}"]`)
    );
    current?.classList.add('selected');
  }
}

customElements.define('colour-swatches-stitch-product', ColourSwatchesProduct);
